import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "@/components/base/Badge";
import { useSession } from "@/auth/session";
import { getDteUrl } from "@/lib/runtime-config";
import BillingCancellationModal from "./components/BillingCancellationModal";
import {
  getBillingClosurePreview,
  createPeriodVariableCharges,
  deletePeriodVariableCharge,
  downloadBillingDocumentsZip,
  exportBillingReportCsv,
  getBillingFolioAvailability,
  getBillingMetrics,
  getBillingDocumentFileUrl,
  getSettingsSnapshot,
  listBillingDocuments,
  listContracts,
  listExpenseConcepts,
  listInvoiceJobs,
  listMeterReadings,
  listPeriodVariableCharges,
  getInvoiceProcessingRun,
  listReadingPeriods,
  processSelectedInvoiceJobs,
  regenerateSelectedBillingDocuments,
  retryInvoiceJob,
  runBillingPeriodClosure,
  startInvoiceProcessingRun,
  updateInvoiceIntegrationSettings,
  type AdminSession,
  type BillingDocumentItem,
  type BillingClosurePreviewItem,
  type ContractListItem,
  type ExpenseConceptItem,
  type FolioAvailabilitySummary,
  type InvoiceProcessingRun,
  type PeriodVariableChargeItem,
  type ReadingPeriodItem,
} from "@/lib/api";

const estadoVariant: Record<string, "success" | "warning" | "error" | "neutral"> = {
  issued: "success",
  pending_external: "warning",
  external_error: "error",
  draft: "neutral",
};

/** Cada cuanto la pantalla consulta el avance de la corrida de emision. */
const RUN_POLL_INTERVAL_MS = 3000;

function isRunActive(run: InvoiceProcessingRun): boolean {
  return run.phase === "emitting" || run.phase === "validating";
}
const DTE_DOCUMENT_TYPES = [
  { value: "33", label: "33 - Factura afecta" },
  { value: "34", label: "34 - Factura exenta" },
  { value: "39", label: "39 - Boleta afecta" },
  { value: "41", label: "41 - Boleta exenta" },
] as const;

type DteDocumentType = (typeof DTE_DOCUMENT_TYPES)[number]["value"];
type BillingSelectionMode = "document_type" | "contract";

export default function FacturacionPage() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [periods, setPeriods] = useState<ReadingPeriodItem[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [documents, setDocuments] = useState<BillingDocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [wizardError, setWizardError] = useState("");
  const [summary, setSummary] = useState("");
  const [showIssued, setShowIssued] = useState(true);
  const [documentQuery, setDocumentQuery] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("");
  const [documentStatusFilter, setDocumentStatusFilter] = useState("");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [metrics, setMetrics] = useState<{
    totals: { documents: number; issued: number; failed: number; pending: number };
    jobs: { total: number; totalAttempts: number; retryJobs: number; retryRatePercent: number };
    performance: { avgLatencyMs: number };
  } | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [foliosLoading, setFoliosLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [closureMessage, setClosureMessage] = useState("");
  const [forceProcess, setForceProcess] = useState(false);
  const [activeContractsCount, setActiveContractsCount] = useState(0);
  const [takenReadingsCount, setTakenReadingsCount] = useState(0);
  const [pendingReadingsCount, setPendingReadingsCount] = useState(0);
  const [processingRun, setProcessingRun] = useState<InvoiceProcessingRun | null>(null);
  const [periodStatusForClosure, setPeriodStatusForClosure] = useState("");
  const [allContracts, setAllContracts] = useState<ContractListItem[]>([]);
  const [variableConcepts, setVariableConcepts] = useState<ExpenseConceptItem[]>([]);
  const [variableCharges, setVariableCharges] = useState<PeriodVariableChargeItem[]>([]);
  const [contractQuery, setContractQuery] = useState("");
  const [selectedContractId, setSelectedContractId] = useState("");
  const [selectedExpenseConceptId, setSelectedExpenseConceptId] = useState("");
  const [variableAmount, setVariableAmount] = useState("");
  const [savingVariableCharge, setSavingVariableCharge] = useState(false);
  const [variableChargeQuery, setVariableChargeQuery] = useState("");
  const [folioAvailability, setFolioAvailability] = useState<FolioAvailabilitySummary | null>(null);
  const [selectedDteTypes, setSelectedDteTypes] = useState<DteDocumentType[]>(DTE_DOCUMENT_TYPES.map((type) => type.value));
  const [billingSelectionMode, setBillingSelectionMode] = useState<BillingSelectionMode>("document_type");
  const [closurePreviewLoading, setClosurePreviewLoading] = useState(false);
  const [closurePreviewRows, setClosurePreviewRows] = useState<BillingClosurePreviewItem[]>([]);
  const [closureContractQuery, setClosureContractQuery] = useState("");
  const [selectedClosureContractIds, setSelectedClosureContractIds] = useState<string[]>([]);
  const [previewPanelRow, setPreviewPanelRow] = useState<BillingClosurePreviewItem | null>(null);
  const [cancellationModalOpen, setCancellationModalOpen] = useState(false);

  async function loadDocumentsAndMetrics(periodId: string): Promise<void> {
    if (!session || !periodId) return;
    setLoading(true);
    setError("");
    setWizardError("");
    try {
      const [docs, metricsPayload] = await Promise.all([
        listBillingDocuments(session, periodId),
        getBillingMetrics(session, periodId),
      ]);
      setDocuments(docs);
      setSelectedDocumentIds([]);
      setMetrics(metricsPayload);
    } catch (loadError) {
      setError(String(loadError));
    } finally {
      setLoading(false);
    }
  }

  async function loadData(periodId?: string): Promise<void> {
    await loadDocumentsAndMetrics(periodId ?? selectedPeriodId);
  }

  // Carga periodos una sola vez al iniciar sesión
  useEffect(() => {
    if (!session) return;
    void (async () => {
      try {
        const periodRows = await listReadingPeriods(session);
        setPeriods(periodRows);
        if (periodRows.length > 0) {
          setSelectedPeriodId(periodRows[0].id);
        }
      } catch (e) {
        setError(String(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Si hay una corrida de emision en curso (p.ej. se recargo la pagina), se
  // retoma su seguimiento en vez de dejar lanzar otra.
  useEffect(() => {
    if (!session) return;
    void (async () => {
      try {
        const run = await getInvoiceProcessingRun(session);
        if (run && isRunActive(run)) await followProcessingRun(run);
      } catch {
        // Sin corrida que retomar: la pantalla queda como siempre.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Carga documentos y métricas cuando cambia el periodo
  useEffect(() => {
    if (!session || !selectedPeriodId) return;
    void loadDocumentsAndMetrics(selectedPeriodId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, selectedPeriodId]);

  const resumen = useMemo(() => {
    if (metrics) {
      return {
        total: metrics.totals.documents,
        issued: metrics.totals.issued,
        errors: metrics.totals.failed,
        pending: metrics.totals.pending,
      };
    }
    const total = documents.length;
    const issued = documents.filter((doc) => doc.status === "issued").length;
    const errors = documents.filter((doc) => doc.status === "external_error").length;
    const pending = total - issued - errors;
    return { total, issued, errors, pending };
  }, [documents, metrics]);

  const visibleDocuments = useMemo(
    () => {
      const query = documentQuery.trim().toLowerCase();
      return documents.filter((doc) => {
        if (!showIssued && doc.status === "issued") return false;
        if (documentStatusFilter && doc.status !== documentStatusFilter) return false;
        if (documentTypeFilter && doc.documentType !== documentTypeFilter) return false;
        if (!query) return true;
        const customer = (doc.customerName ?? "").toLowerCase();
        const contract = (doc.contractNumber ?? "").toLowerCase();
        const folio = (doc.folio ?? "").toLowerCase();
        return customer.includes(query) || contract.includes(query) || folio.includes(query);
      });
    },
    [documentQuery, documentStatusFilter, documentTypeFilter, documents, showIssued],
  );
  const selectableVisibleDocuments = useMemo(
    () => visibleDocuments.filter((doc) => doc.status !== "issued" && Boolean(doc.lastJobId)),
    [visibleDocuments],
  );
  const allVisibleSelected = selectableVisibleDocuments.length > 0 && selectableVisibleDocuments.every((doc) => selectedDocumentIds.includes(doc.id));
  const someVisibleSelected = selectableVisibleDocuments.some((doc) => selectedDocumentIds.includes(doc.id));

  const totalPages = pageSize === 0 ? 1 : Math.ceil(visibleDocuments.length / pageSize);
  const paginatedDocuments = useMemo(() => {
    if (pageSize === 0) return visibleDocuments;
    const start = (currentPage - 1) * pageSize;
    return visibleDocuments.slice(start, start + pageSize);
  }, [visibleDocuments, currentPage, pageSize]);

  // Volver a página 1 al cambiar filtros o periodo
  useEffect(() => {
    setCurrentPage(1);
  }, [documentQuery, documentStatusFilter, documentTypeFilter, showIssued, selectedPeriodId]);

  const filteredContracts = useMemo(() => {
    const q = contractQuery.trim().toLowerCase();
    if (!q) return allContracts.slice(0, 50);
    return allContracts
      .filter((contract) => {
        const contractNumber = (contract.contractNumber ?? "").toLowerCase();
        const customerName = (contract.customerName ?? "").toLowerCase();
        return contractNumber.includes(q) || customerName.includes(q);
      })
      .slice(0, 50);
  }, [allContracts, contractQuery]);

  const filteredVariableCharges = useMemo(() => {
    const q = variableChargeQuery.trim().toLowerCase();
    if (!q) return variableCharges;
    return variableCharges.filter((entry) => {
      const customer = (entry.customerName ?? "").toLowerCase();
      const contract = (entry.contractNumber ?? "").toLowerCase();
      return customer.includes(q) || contract.includes(q);
    });
  }, [variableCharges, variableChargeQuery]);

  const requiredByDteType = useMemo(() => {
    const base: Record<DteDocumentType, number> = { "33": 0, "34": 0, "39": 0, "41": 0 };
    for (const row of closurePreviewRows) {
      base[row.documentType] += 1;
    }
    return base;
  }, [closurePreviewRows]);

  const selectedRequiredByDteType = useMemo(() => {
    const selected = new Set(selectedClosureContractIds);
    const sourceRows =
      billingSelectionMode === "contract"
        ? closurePreviewRows.filter((row) => selected.has(row.contractId))
        : closurePreviewRows;
    return sourceRows.reduce<Record<DteDocumentType, number>>(
      (acc, row) => {
        acc[row.documentType] += 1;
        return acc;
      },
      { "33": 0, "34": 0, "39": 0, "41": 0 },
    );
  }, [billingSelectionMode, closurePreviewRows, selectedClosureContractIds]);

  const filteredClosurePreviewRows = useMemo(() => {
    const q = closureContractQuery.trim().toLowerCase();
    if (!q) return closurePreviewRows;
    return closurePreviewRows.filter((row) => {
      const customerName = (row.customerName ?? "").toLowerCase();
      const contractNumber = (row.contractNumber ?? "").toLowerCase();
      return customerName.includes(q) || contractNumber.includes(q);
    });
  }, [closureContractQuery, closurePreviewRows]);

  const selectedContractsCount = useMemo(
    () => (billingSelectionMode === "contract" ? selectedClosureContractIds.length : closurePreviewRows.length),
    [billingSelectionMode, closurePreviewRows.length, selectedClosureContractIds.length],
  );

  const allVisibleClosureContractsSelected =
    filteredClosurePreviewRows.length > 0 &&
    filteredClosurePreviewRows.every((row) => selectedClosureContractIds.includes(row.contractId));

  async function addVariableCharge(): Promise<void> {
    if (!session || !selectedPeriodId) return;
    const amount = Number(variableAmount);
    if (!selectedContractId || !selectedExpenseConceptId || !Number.isFinite(amount) || amount <= 0) {
      setWizardError("Selecciona contrato, gasto variable y un valor mayor a 0.");
      return;
    }
    setSavingVariableCharge(true);
    setWizardError("");
    try {
      const created = await createPeriodVariableCharges(session, {
        readingPeriodId: selectedPeriodId,
        charges: [{ contractId: selectedContractId, expenseConceptId: selectedExpenseConceptId, amount }],
      });
      setVariableCharges((prev) => [...created, ...prev]);
      setVariableAmount("");
    } catch (e) {
      setWizardError(e instanceof Error ? e.message : "No se pudo guardar gasto variable");
    } finally {
      setSavingVariableCharge(false);
    }
  }

  async function removeVariableCharge(id: string): Promise<void> {
    if (!session) return;
    try {
      await deletePeriodVariableCharge(session, id);
      setVariableCharges((prev) => prev.filter((entry) => entry.id !== id));
    } catch (e) {
      setWizardError(e instanceof Error ? e.message : "No se pudo eliminar gasto variable");
    }
  }

  async function openClosureWizard(): Promise<void> {
    if (!session || !selectedPeriodId) {
      setError("Selecciona un periodo de lectura.");
      return;
    }
    setError("");
    setWizardError("");
    setProcessingRun(null);
    setWizardStep(1);
    setForceProcess(false);
    setContractQuery("");
    setSelectedContractId("");
    setVariableAmount("");
    setSelectedDteTypes(DTE_DOCUMENT_TYPES.map((type) => type.value));
    setBillingSelectionMode("document_type");
    setClosureContractQuery("");
    setSelectedClosureContractIds([]);
    setClosurePreviewRows([]);
    setPreviewPanelRow(null);
    setWizardOpen(true);
    setWizardLoading(true);
    try {
      const [settings, contracts, readings, expenseConcepts, existingVariableCharges, folios] = await Promise.all([
        getSettingsSnapshot(session),
        listContracts(session),
        listMeterReadings(session, selectedPeriodId),
        listExpenseConcepts(session),
        listPeriodVariableCharges(session, selectedPeriodId),
        getBillingFolioAvailability(session, selectedPeriodId, false),
      ]);
      const activeContracts = contracts.filter((c) => c.status === "active");
      const variableOnly = expenseConcepts.filter((c) => c.conceptType === "variable" && c.status === "active");
      setAllContracts(activeContracts);
      setVariableConcepts(variableOnly);
      setVariableCharges(existingVariableCharges);
      setFolioAvailability(folios);
      setSelectedExpenseConceptId(variableOnly[0]?.id ?? "");
      const selectedPeriod = periods.find((p) => p.id === selectedPeriodId);
      setPeriodStatusForClosure(selectedPeriod?.status ?? "");
      const takenContracts = new Set(readings.map((r) => r.contractId));
      setActiveContractsCount(activeContracts.length);
      setTakenReadingsCount(takenContracts.size);
      setPendingReadingsCount(Math.max(0, activeContracts.length - takenContracts.size));
      const extraJson = (settings.invoiceIntegration?.extraJson ?? {}) as Record<string, unknown>;
      setClosureMessage(
        typeof extraJson.periodClosureMessage === "string"
          ? (extraJson.periodClosureMessage as string)
          : "Estimado(a) cliente, su cuenta corresponde al cierre del período vigente.",
      );
    } catch (e) {
      setWizardError(e instanceof Error ? e.message : "No se pudo preparar cierre");
      setWizardOpen(false);
    } finally {
      setWizardLoading(false);
    }
  }

  async function executeClosure(): Promise<void> {
    if (!session || !selectedPeriodId) return;
    if (selectedDteTypes.length === 0) {
      setWizardError("Selecciona al menos un tipo de documento DTE para emitir.");
      setWizardStep(5);
      return;
    }
    if (billingSelectionMode === "contract" && selectedClosureContractIds.length === 0) {
      setWizardError("Selecciona al menos un contrato para facturar.");
      setWizardStep(5);
      return;
    }
    if (selectedContractsCount === 0) {
      setWizardError(
        billingSelectionMode === "contract"
          ? "No hay contratos seleccionados para facturar."
          : "No hay contratos activos para los tipos DTE seleccionados.",
      );
      setWizardStep(5);
      return;
    }
    if (pendingReadingsCount > 0 && !forceProcess) {
      setWizardError("Debes confirmar la emisión para contratos sin lectura antes de continuar.");
      setWizardStep(2);
      return;
    }
    let folioSnapshot: FolioAvailabilitySummary;
    setFoliosLoading(true);
    try {
      folioSnapshot = await getBillingFolioAvailability(session, selectedPeriodId, false);
    } catch (e) {
      setWizardError(e instanceof Error ? e.message : "No se pudo consultar disponibilidad de folios.");
      setWizardStep(1);
      setFoliosLoading(false);
      return;
    }
    setFoliosLoading(false);
    setFolioAvailability(folioSnapshot);
    if (folioSnapshot.items.some((item) => item.available < (selectedRequiredByDteType[normalizeDteDocumentType(String(item.type))] ?? 0))) {
      setWizardError("No hay folios suficientes para los tipos de documento seleccionados.");
      setWizardStep(5);
      return;
    }
    setProcessing(true);
    setWizardError("");
    try {
      const settings = await getSettingsSnapshot(session);
      const extraJson = (settings.invoiceIntegration?.extraJson ?? {}) as Record<string, unknown>;
      await updateClosureMessage(session, settings.invoiceIntegration, extraJson, closureMessage);
      await runBillingPeriodClosure(session, {
        readingPeriodId: selectedPeriodId,
        forceWithoutReading: forceProcess,
        selectionMode: billingSelectionMode,
        documentTypes: selectedDteTypes,
        contractIds: billingSelectionMode === "contract" ? selectedClosureContractIds : undefined,
      });
      const run = await startInvoiceProcessingRun(
        session,
        selectedDteTypes,
        billingSelectionMode === "contract" ? selectedClosureContractIds : undefined,
      );
      setWizardStep(6);
      await followProcessingRun(run);
    } catch (runError) {
      setWizardError(runError instanceof Error ? runError.message : String(runError));
    } finally {
      setProcessing(false);
    }
  }

  /**
   * Sigue la corrida unica (emision de todos los pendientes + validacion SII)
   * hasta que termina, mostrando su avance. Reemplaza el "procesar siguiente
   * lote": la corrida vive en el servidor, asi que cerrar la pestana no la
   * corta y volver a la pagina retoma el seguimiento.
   */
  async function followProcessingRun(initial: InvoiceProcessingRun): Promise<void> {
    if (!session) return;
    setProcessing(true);
    let run: InvoiceProcessingRun | null = initial;
    try {
      while (run && isRunActive(run)) {
        setProcessingRun(run);
        await new Promise((resolve) => setTimeout(resolve, RUN_POLL_INTERVAL_MS));
        run = await getInvoiceProcessingRun(session);
      }
      if (run) {
        setProcessingRun(run);
        setSummary(
          `${run.message}. Emitidos: ${run.issued}, errores/rechazos: ${run.failed}, pendientes SII: ${run.pendingSii}.`,
        );
        if (run.phase === "failed") setError(run.error ?? run.message);
      }
      await loadData(selectedPeriodId || undefined);
    } finally {
      setProcessing(false);
    }
  }

  async function handleReintentar(doc: BillingDocumentItem): Promise<void> {
    if (!session || !doc.lastJobId) return;
    try {
      await retryInvoiceJob(session, doc.lastJobId);
      await processSelectedInvoiceJobs(session, [doc.lastJobId]);
      await loadData(selectedPeriodId || undefined);
    } catch (retryError) {
      setError(String(retryError));
    }
  }

  /** Emite y valida todo lo que quede pendiente (p.ej. tras una corrida interrumpida). */
  async function handleProcessPending(): Promise<void> {
    if (!session) return;
    setError("");
    try {
      const run = await startInvoiceProcessingRun(
        session,
        selectedDteTypes,
        billingSelectionMode === "contract" ? selectedClosureContractIds : undefined,
      );
      await followProcessingRun(run);
    } catch (runError) {
      setError(runError instanceof Error ? runError.message : String(runError));
    }
  }

  async function openDocumentFile(doc: BillingDocumentItem, kind: "pdf" | "xml", download = false): Promise<void> {
    if (!session) return;
    try {
      const file = await getBillingDocumentFileUrl(session, doc.id, kind);
      if (!download) {
        window.open(file.url, "_blank", "noopener,noreferrer");
        return;
      }

      const response = await fetch(file.url);
      if (!response.ok) {
        window.open(file.url, "_blank", "noopener,noreferrer");
        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (fileError) {
      try {
        const fallback = await getBillingDocumentFileUrl(session, doc.id, kind);
        window.open(fallback.url, "_blank", "noopener,noreferrer");
      } catch (fallbackError) {
        setError(fallbackError instanceof Error ? fallbackError.message : String(fallbackError));
      }
    }
  }

  async function loadClosurePreview(nextDocumentTypes = selectedDteTypes): Promise<void> {
    if (!session || !selectedPeriodId) return;
    setClosurePreviewLoading(true);
    try {
      const rows = await getBillingClosurePreview(session, selectedPeriodId, nextDocumentTypes);
      setClosurePreviewRows(rows);
      setSelectedClosureContractIds((prev) => prev.filter((id) => rows.some((row) => row.contractId === id)));
      setPreviewPanelRow((prev) => (prev ? rows.find((row) => row.contractId === prev.contractId) ?? null : null));
    } catch (e) {
      setWizardError(e instanceof Error ? e.message : "No se pudo cargar preview de facturacion");
    } finally {
      setClosurePreviewLoading(false);
    }
  }

  useEffect(() => {
    if (!wizardOpen || !session || !selectedPeriodId) return;
    void loadClosurePreview(selectedDteTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardOpen, session, selectedPeriodId, selectedDteTypes.join(",")]);

  async function downloadDocumentsZip(input: { readingPeriodId?: string; documentIds?: string[] }): Promise<void> {
    if (!session) return;
    try {
      const payload = await downloadBillingDocumentsZip(session, input);
      const url = URL.createObjectURL(payload.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = payload.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (zipError) {
      setError(zipError instanceof Error ? zipError.message : String(zipError));
    }
  }

  function toggleDocumentSelection(doc: BillingDocumentItem): void {
    if (!doc.lastJobId || doc.status === "issued") return;
    setSelectedDocumentIds((prev) =>
      prev.includes(doc.id) ? prev.filter((id) => id !== doc.id) : [...prev, doc.id],
    );
  }

  function toggleAllVisibleDocuments(): void {
    const visibleIds = selectableVisibleDocuments.map((doc) => doc.id);
    if (visibleIds.length === 0) return;
    setSelectedDocumentIds((prev) => {
      const visibleSet = new Set(visibleIds);
      if (visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleSet.has(id));
      }
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  }

  async function handleReprocessSelected(): Promise<void> {
    if (!session || selectedDocumentIds.length === 0) return;
    const jobIds = documents
      .filter((doc) => selectedDocumentIds.includes(doc.id) && doc.lastJobId)
      .map((doc) => doc.lastJobId)
      .filter((jobId): jobId is string => Boolean(jobId));
    if (jobIds.length === 0) {
      setError("No hay jobs validos seleccionados para reprocesar.");
      return;
    }

    try {
      await processSelectedInvoiceJobs(session, jobIds);
      setSelectedDocumentIds([]);
      await loadData(selectedPeriodId || undefined);
    } catch (reprocessError) {
      setError(reprocessError instanceof Error ? reprocessError.message : String(reprocessError));
    }
  }

  async function handleRegenerateSelected(): Promise<void> {
    if (!session || selectedDocumentIds.length === 0) return;
    if (!window.confirm("Se eliminaran los documentos seleccionados y su job para volver a generarlos. Deseas continuar?")) {
      return;
    }

    try {
      await regenerateSelectedBillingDocuments(session, selectedDocumentIds);
      setSelectedDocumentIds([]);
      await loadData(selectedPeriodId || undefined);
    } catch (regenerateError) {
      setError(regenerateError instanceof Error ? regenerateError.message : String(regenerateError));
    }
  }

  async function handleRefreshJobs(): Promise<void> {
    if (!session) return;
    try {
      const jobs = await listInvoiceJobs(session);
      setSummary(`Jobs cargados: ${jobs.length}`);
    } catch (refreshError) {
      setError(String(refreshError));
    }
  }

  function handleViewPendingReadings(): void {
    if (!selectedPeriodId) return;
    const params = new URLSearchParams({
      readingPeriodId: selectedPeriodId,
      estado: "pendiente",
    });
    navigate(`/lecturas-periodo?${params.toString()}`);
  }

  function toggleDteType(type: DteDocumentType): void {
    setSelectedDteTypes((prev) =>
      prev.includes(type) ? prev.filter((entry) => entry !== type) : [...prev, type].sort(),
    );
    setWizardError("");
  }

  function toggleClosureContract(contractId: string): void {
    setSelectedClosureContractIds((prev) =>
      prev.includes(contractId) ? prev.filter((entry) => entry !== contractId) : [...prev, contractId],
    );
    setWizardError("");
  }

  function toggleAllVisibleClosureContracts(): void {
    const visibleIds = filteredClosurePreviewRows.map((row) => row.contractId);
    if (visibleIds.length === 0) return;
    setSelectedClosureContractIds((prev) => {
      const visibleSet = new Set(visibleIds);
      if (visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleSet.has(id));
      }
      return Array.from(new Set([...prev, ...visibleIds]));
    });
    setWizardError("");
  }

  async function handleExport(): Promise<void> {
    if (!session) return;
    try {
      const payload = await exportBillingReportCsv(session, selectedPeriodId || undefined);
      const blob = new Blob([payload.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = payload.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(String(exportError));
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Facturación</h1>
          <p className="text-sm text-gray-500 mt-0.5">Cierre de período, emisión DTE y seguimiento de jobs</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => void handleExport()} className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Exportar CSV</button>
          <button onClick={() => void downloadDocumentsZip({ readingPeriodId: selectedPeriodId || undefined })} disabled={!selectedPeriodId || resumen.issued === 0} className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">Descargar ZIP DTE</button>
          <button onClick={() => setCancellationModalOpen(true)} className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Anulación</button>
          <button onClick={() => void handleRefreshJobs()} className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Actualizar jobs</button>
          <button onClick={() => void handleProcessPending()} disabled={processing || resumen.pending === 0} className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">Procesar pendientes</button>
          <button onClick={() => void openClosureWizard()} disabled={processing || !selectedPeriodId} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-60">
            Cierre de período
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total" value={resumen.total} color="text-blue-600" bg="bg-blue-50" icon="ri-file-list-3-line" />
        <StatCard label="Emitidos" value={resumen.issued} color="text-emerald-600" bg="bg-emerald-50" icon="ri-checkbox-circle-line" />
        <StatCard label="Pendientes" value={resumen.pending} color="text-amber-600" bg="bg-amber-50" icon="ri-time-line" />
        <StatCard label="Errores" value={resumen.errors} color="text-red-600" bg="bg-red-50" icon="ri-error-warning-line" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Periodo:</label>
        <select value={selectedPeriodId} onChange={(event) => setSelectedPeriodId(event.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          <option value="">Selecciona periodo</option>
          {periods.map((period) => (
            <option key={period.id} value={period.id}>
              {period.periodCode} ({period.status})
            </option>
          ))}
        </select>
        <input
          value={documentQuery}
          onChange={(e) => setDocumentQuery(e.target.value)}
          placeholder="Filtrar por cliente, contrato o folio"
          className="ml-auto text-sm border border-gray-200 rounded-lg px-3 py-2 w-72"
        />
        <select value={documentTypeFilter} onChange={(e) => setDocumentTypeFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          <option value="">Todos los tipos</option>
          <option value="33">33 - Factura afecta</option>
          <option value="34">34 - Factura exenta</option>
          <option value="39">39 - Boleta afecta</option>
          <option value="41">41 - Boleta exenta</option>
        </select>
        <select value={documentStatusFilter} onChange={(e) => setDocumentStatusFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          <option value="">Todos los estados</option>
          <option value="draft">draft</option>
          <option value="pending_external">pending_external</option>
          <option value="external_error">external_error</option>
          <option value="issued">issued</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={showIssued} onChange={(e) => setShowIssued(e.target.checked)} />
          Mostrar emitidos
        </label>
        <button
          onClick={() => void handleReprocessSelected()}
          disabled={selectedDocumentIds.length === 0}
          className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          Reprocesar seleccionados ({selectedDocumentIds.length})
        </button>
        <button
          onClick={() => void handleRegenerateSelected()}
          disabled={selectedDocumentIds.length === 0}
          className="px-3 py-2 text-sm font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 disabled:opacity-50"
        >
          Regenerar ({selectedDocumentIds.length})
        </button>
      </div>

      {summary ? <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">{summary}</div> : null}
      {error ? <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div> : null}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someVisibleSelected && !allVisibleSelected;
                    }}
                    onChange={toggleAllVisibleDocuments}
                    disabled={selectableVisibleDocuments.length === 0}
                    title="Seleccionar visibles"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contrato</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Folio</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Monto</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Error</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-sm text-gray-500">Cargando documentos...</td></tr>
              ) : visibleDocuments.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-sm text-gray-500">No hay documentos visibles para el período.</td></tr>
              ) : (
                paginatedDocuments.map((doc, index) => (
                  <tr key={doc.id} className={`border-b border-gray-50 ${index % 2 === 1 ? "bg-gray-50/30" : ""}`}>
                    <td className="px-3 py-3 text-center">
                      {doc.status !== "issued" && doc.lastJobId ? (
                        <input
                          type="checkbox"
                          checked={selectedDocumentIds.includes(doc.id)}
                          onChange={() => toggleDocumentSelection(doc)}
                        />
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{doc.customerName || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>{doc.contractNumber || "-"}</div>
                      <div className="text-xs text-gray-400">{doc.contractAddress || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{doc.documentType}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{doc.folio || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 text-right">${doc.totalAmount.toLocaleString("es-CL")}</td>
                    <td className="px-4 py-3 text-center"><Badge variant={estadoVariant[doc.status] ?? "neutral"} size="sm">{doc.status}</Badge></td>
                    <td className="px-4 py-3 text-xs text-red-600">{(doc.externalError && !doc.externalError.includes("ALREADY_ISSUED") && !doc.externalError.toLowerCase().includes("ya emitido")) ? doc.externalError : "-"}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <button onClick={() => void openDocumentFile(doc, "pdf", false)} className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                          Ver PDF
                        </button>
                        <button onClick={() => void openDocumentFile(doc, "pdf", true)} className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                          Descargar PDF
                        </button>
                        <button onClick={() => void openDocumentFile(doc, "xml", false)} className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                          Ver XML
                        </button>
                        <button onClick={() => void openDocumentFile(doc, "xml", true)} className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                          Descargar XML
                        </button>
                        {doc.status === "external_error" && doc.lastJobId ? (
                          <button onClick={() => void handleReintentar(doc)} className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600">
                            Reintentar
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 mr-1">Mostrar:</span>
            {[20, 40, 60, 100, 0].map((size) => (
              <button
                key={size}
                onClick={() => { setPageSize(size); setCurrentPage(1); }}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium ${pageSize === size ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {size === 0 ? "Todos" : size}
              </button>
            ))}
            <span className="ml-2 text-xs text-gray-400">{visibleDocuments.length} registros</span>
          </div>
          {pageSize > 0 && totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              <span className="px-3 py-1 text-xs text-gray-600">Página {currentPage} de {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          )}
        </div>
      </div>

      {wizardOpen ? (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-3xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Cierre de período</h2>
              <button
                onClick={() => {
                  if (wizardLoading || foliosLoading || processing) return;
                  setWizardOpen(false);
                  setWizardError("");
                  setPreviewPanelRow(null);
                }}
                disabled={wizardLoading || foliosLoading || processing}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="text-xs text-gray-500">Paso {wizardStep} de 6</div>
            {wizardError ? (
              <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                {wizardError}
              </div>
            ) : null}
            {wizardLoading || foliosLoading ? (
              <div className="p-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                Cargando datos del cierre...
              </div>
            ) : null}

            {wizardStep === 1 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Revisión de contratos activos para generar documentos.</p>
                <div className={`p-3 text-sm rounded-lg border ${
                  periodStatusForClosure === "closed"
                    ? "text-emerald-800 bg-emerald-50 border-emerald-200"
                    : "text-amber-800 bg-amber-50 border-amber-200"
                }`}>
                  Estado del periodo: <strong>{periodStatusForClosure || "sin definir"}</strong>.
                  {periodStatusForClosure === "closed"
                    ? " Puedes continuar."
                    : " Se cerrará automáticamente al procesar."}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <MiniStat label="Activos" value={activeContractsCount} />
                  <MiniStat label="Lecturas tomadas" value={takenReadingsCount} />
                  <MiniStat label="Pendientes" value={pendingReadingsCount} />
                </div>
                {pendingReadingsCount > 0 ? (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleViewPendingReadings}
                      className="px-3 py-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
                    >
                      Ver lecturas pendientes
                    </button>
                  </div>
                ) : null}
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">DTE</th>
                        <th className="px-3 py-2 text-right">Requeridos</th>
                        <th className="px-3 py-2 text-right">Disponibles</th>
                        <th className="px-3 py-2 text-right">Folio actual</th>
                        <th className="px-3 py-2 text-right">Folio final</th>
                        <th className="px-3 py-2 text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(folioAvailability?.items ?? []).map((item) => (
                        <tr key={item.type} className="border-t border-gray-100">
                          <td className="px-3 py-2">{item.type}</td>
                          <td className="px-3 py-2 text-right">{requiredByDteType[String(item.type) as "33" | "34" | "39" | "41"] ?? 0}</td>
                          <td className="px-3 py-2 text-right">{item.available}</td>
                          <td className="px-3 py-2 text-right">{item.folioActual ?? "-"}</td>
                          <td className="px-3 py-2 text-right">{item.folioFinal ?? "-"}</td>
                          <td
                            className={`px-3 py-2 text-center font-medium ${
                              item.available >= (requiredByDteType[String(item.type) as "33" | "34" | "39" | "41"] ?? 0)
                                ? "text-emerald-700"
                                : "text-red-700"
                            }`}
                          >
                            {item.available >= (requiredByDteType[String(item.type) as "33" | "34" | "39" | "41"] ?? 0) ? "OK" : "INSUFICIENTE"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {folioAvailability &&
                folioAvailability.items.some(
                  (item) => item.available < (requiredByDteType[String(item.type) as "33" | "34" | "39" | "41"] ?? 0),
                ) ? (
                  <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
                    Aviso informativo: algunos tipos DTE no tienen folios suficientes. La validacion final se hara solo con los tipos seleccionados en el ultimo paso.
                  </div>
                ) : null}
              </div>
            ) : null}

            {wizardStep === 2 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Validación de lecturas tomadas.</p>
                {pendingReadingsCount > 0 ? (
                  <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
                    Hay {pendingReadingsCount} contratos sin lectura: se facturarán con consumo 0, manteniendo gasto fijo y gastos variables.
                  </div>
                ) : (
                  <div className="p-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg">
                    Todas las lecturas están completas.
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={forceProcess} onChange={(e) => setForceProcess(e.target.checked)} />
                  Confirmo emitir DTE para contratos sin lectura (consumo 0)
                </label>
              </div>
            ) : null}

            {wizardStep === 3 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Mensaje de cierre que se mostrará en la factura.</p>
                <textarea value={closureMessage} onChange={(e) => setClosureMessage(e.target.value)} rows={4} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                <div className="p-3 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg">
                  Al continuar, el sistema bloqueará el período para ejecutar el proceso.
                </div>
              </div>
            ) : null}

            {wizardStep === 4 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Agregar gastos variables por contrato para este período.</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div className="md:col-span-2">
                    <input value={contractQuery} onChange={(e) => setContractQuery(e.target.value)} placeholder="Buscar por N° contrato o nombre cliente" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                  </div>
                  <div>
                    <select value={selectedContractId} onChange={(e) => setSelectedContractId(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                      <option value="">Selecciona contrato</option>
                      {filteredContracts.map((contract) => (
                        <option key={contract.id} value={contract.id}>{contract.contractNumber} - {contract.customerName || "Sin nombre"}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select value={selectedExpenseConceptId} onChange={(e) => setSelectedExpenseConceptId(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                      <option value="">Selecciona gasto variable</option>
                      {variableConcepts.map((concept) => (
                        <option key={concept.id} value={concept.id}>{concept.name} {concept.isGross ? "- Bruto" : "- Neto"}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" step="0.01" value={variableAmount} onChange={(e) => setVariableAmount(e.target.value)} placeholder="Valor" className="w-40 px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                  <button type="button" onClick={() => void addVariableCharge()} disabled={savingVariableCharge} className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-60">
                    {savingVariableCharge ? "Guardando..." : "Agregar gasto"}
                  </button>
                </div>
                <div className="space-y-2">
                  <input
                    value={variableChargeQuery}
                    onChange={(e) => setVariableChargeQuery(e.target.value)}
                    placeholder="Filtrar listado por nombre o N° contrato"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                  />
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-72 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">Cliente</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">Contrato</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">Gasto</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">Valor</th>
                        <th className="px-3 py-2 text-center text-xs text-gray-500">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVariableCharges.length === 0 ? (
                        <tr><td colSpan={5} className="px-3 py-3 text-sm text-gray-500 text-center">Sin gastos variables cargados.</td></tr>
                      ) : filteredVariableCharges.map((entry) => (
                        <tr key={entry.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 text-sm">{entry.customerName || "-"}</td>
                          <td className="px-3 py-2 text-sm">{entry.contractNumber || "-"}</td>
                          <td className="px-3 py-2 text-sm">{entry.expenseConceptName || "-"}</td>
                          <td className="px-3 py-2 text-sm text-right">${entry.amount.toLocaleString("es-CL")}</td>
                          <td className="px-3 py-2 text-center"><button type="button" onClick={() => void removeVariableCharge(entry.id)} className="text-xs text-red-600 hover:text-red-700">Eliminar</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
                </div>
              </div>
            ) : null}

            {wizardStep === 5 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Selecciona los tipos de documento DTE y define si el cierre se ejecuta por tipo o por cliente.</p>
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 p-1 w-full md:w-fit">
                  <button
                    type="button"
                    onClick={() => setBillingSelectionMode("document_type")}
                    className={`px-3 py-2 text-sm rounded-lg ${billingSelectionMode === "document_type" ? "bg-blue-600 text-white" : "text-gray-600"}`}
                  >
                    Por tipos de documento
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingSelectionMode("contract")}
                    className={`px-3 py-2 text-sm rounded-lg ${billingSelectionMode === "contract" ? "bg-blue-600 text-white" : "text-gray-600"}`}
                  >
                    Por cliente
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {DTE_DOCUMENT_TYPES.map((type) => {
                    const required = requiredByDteType[type.value];
                    const availability = folioAvailability?.items.find((item) => normalizeDteDocumentType(String(item.type)) === type.value);
                    const selected = selectedDteTypes.includes(type.value);
                    return (
                      <label
                        key={type.value}
                        className={`flex items-start gap-3 rounded-xl border p-3 text-sm ${
                          selected ? "border-emerald-200 bg-emerald-50" : "border-gray-200 bg-white"
                        }`}
                      >
                        <input type="checkbox" checked={selected} onChange={() => toggleDteType(type.value)} className="mt-1" />
                        <span className="flex-1">
                          <span className="block font-semibold text-gray-800">{type.label}</span>
                          <span className="block text-xs text-gray-500">Contratos: {required}</span>
                          <span className="block text-xs text-gray-500">Folios disponibles: {availability?.available ?? 0}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="p-3 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg">
                  Se crearan y enviaran al dte_cloud solo los documentos seleccionados. Si eliges facturar por cliente, la tabla mostrara solo contratos de los tipos marcados.
                </div>
                {billingSelectionMode === "contract" ? (
                  <div className="space-y-3 rounded-xl border border-gray-200 p-3">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div className="text-sm font-medium text-gray-700">Seleccion individual de contratos</div>
                      <div className="flex flex-col gap-2 md:flex-row md:items-center">
                        <input
                          value={closureContractQuery}
                          onChange={(e) => setClosureContractQuery(e.target.value)}
                          placeholder="Filtrar por nombre o contrato"
                          className="w-full md:w-72 px-3 py-2 text-sm border border-gray-200 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={toggleAllVisibleClosureContracts}
                          disabled={filteredClosurePreviewRows.length === 0}
                          className="px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-50"
                        >
                          {allVisibleClosureContractsSelected ? "Desmarcar visibles" : "Marcar visibles"}
                        </button>
                      </div>
                    </div>
                    {closurePreviewLoading ? (
                      <div className="p-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg">
                        Cargando calculo por contrato...
                      </div>
                    ) : null}
                    <div className="max-h-80 overflow-auto rounded-lg border border-gray-200">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-center">Check</th>
                            <th className="px-3 py-2 text-left">Nombre</th>
                            <th className="px-3 py-2 text-left">Contrato</th>
                            <th className="px-3 py-2 text-right">$ Facturado en el periodo</th>
                            <th className="px-3 py-2 text-right">m3 consumo</th>
                            <th className="px-3 py-2 text-right">Calculo de consumo a facturar</th>
                            <th className="px-3 py-2 text-right">Calculo a facturar actualizado</th>
                            <th className="px-3 py-2 text-center">Vista</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredClosurePreviewRows.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-3 py-4 text-center text-sm text-gray-500">
                                No hay contratos disponibles para los tipos seleccionados.
                              </td>
                            </tr>
                          ) : (
                            filteredClosurePreviewRows.map((row) => (
                              <tr key={row.contractId} className="border-t border-gray-100">
                                <td className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedClosureContractIds.includes(row.contractId)}
                                    onChange={() => toggleClosureContract(row.contractId)}
                                  />
                                </td>
                                <td className="px-3 py-2">{row.customerName || "-"}</td>
                                <td className="px-3 py-2">
                                  <div className="font-medium text-gray-700">{row.contractNumber}</div>
                                  <div className="text-[11px] text-gray-400">DTE {row.documentType}</div>
                                </td>
                                <td className="px-3 py-2 text-right">${row.billedInPeriod.toLocaleString("es-CL")}</td>
                                <td className="px-3 py-2 text-right">{row.consumptionM3.toLocaleString("es-CL")}</td>
                                <td className="px-3 py-2 text-right">${row.currentConsumptionCharge.toLocaleString("es-CL")}</td>
                                <td className="px-3 py-2 text-right">
                                  <div>${row.updatedConsumptionCharge.toLocaleString("es-CL")}</div>
                                  <div className={`text-[11px] ${row.differenceAmount === 0 ? "text-gray-400" : row.differenceAmount > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                                    Total: ${row.updatedTotalAmount.toLocaleString("es-CL")}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => setPreviewPanelRow(row)}
                                    className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-2 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-50"
                                  >
                                    <i className="ri-file-search-line" />
                                    Ver
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
                {selectedDteTypes.length === 0 ? (
                  <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                    Debes seleccionar al menos un tipo de documento.
                  </div>
                ) : null}
                {billingSelectionMode === "contract" && selectedClosureContractIds.length === 0 ? (
                  <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
                    Debes marcar los contratos que quieres refacturar en este cierre.
                  </div>
                ) : null}
                {folioAvailability &&
                folioAvailability.items.some(
                  (item) => item.available < (selectedRequiredByDteType[normalizeDteDocumentType(String(item.type))] ?? 0),
                ) ? (
                  <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                    No hay folios suficientes para los tipos seleccionados. Al procesar se volvera a validar la disponibilidad actual.
                  </div>
                ) : null}
                <div className="grid grid-cols-3 gap-3">
                  <MiniStat label="Tipos seleccionados" value={selectedDteTypes.length} />
                  <MiniStat label="Contratos pendientes" value={closurePreviewRows.length} />
                  <MiniStat label="Contratos a emitir" value={selectedContractsCount} />
                </div>
              </div>
            ) : null}

            {wizardStep === 6 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Resultado del cierre y generación en DTE.</p>
                {processingRun ? (
                  <>
                    <div className="grid grid-cols-5 gap-3">
                      <MiniStat label="Documentos" value={processingRun.total} />
                      <MiniStat label="Emitidos" value={processingRun.issued} />
                      <MiniStat label="Errores/Rechazos" value={processingRun.failed} />
                      <MiniStat label="Pendientes SII" value={processingRun.pendingSii} />
                      <MiniStat label="Por emitir" value={processingRun.remainingPending} />
                    </div>
                    <p className="text-sm text-gray-700">{processingRun.message}</p>
                    {!isRunActive(processingRun) && processingRun.documentIds.length > 0 ? (
                      <button
                        onClick={() => void downloadDocumentsZip({ documentIds: processingRun.documentIds })}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        Descargar ZIP de los emitidos
                      </button>
                    ) : null}
                  </>
                ) : (
                  <div className="text-sm text-gray-500">Sin resumen disponible aún.</div>
                )}
                <p className="text-xs text-gray-500">Los contratos con emisión exitosa quedan fuera de la vista por defecto de reproceso.</p>
              </div>
            ) : null}
            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setWizardStep((s) => Math.max(1, s - 1))} disabled={wizardStep === 1 || processing || wizardLoading || foliosLoading} className="px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-50">Anterior</button>
              <div className="flex items-center gap-2">
                {wizardStep < 5 ? (
                  <button
                    onClick={() => setWizardStep((s) => Math.min(5, s + 1))}
                    disabled={
                      processing ||
                      wizardLoading ||
                      foliosLoading ||
                      (wizardStep === 2 && pendingReadingsCount > 0 && !forceProcess)
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg disabled:opacity-60"
                  >
                    Siguiente
                  </button>
                ) : null}
                {wizardStep === 5 ? (
                  <button
                    onClick={() => void executeClosure()}
                    disabled={
                      processing ||
                      wizardLoading ||
                      foliosLoading ||
                      selectedDteTypes.length === 0 ||
                      selectedContractsCount === 0
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg disabled:opacity-60"
                  >
                    {processing ? "Procesando periodo..." : "Procesar periodo"}
                  </button>
                ) : null}
                {wizardStep === 6 ? (
                  <button onClick={() => setWizardOpen(false)} className="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg">Cerrar</button>
                ) : null}
              </div>
            </div>
          </div>
          {previewPanelRow ? (
            <BillingPreviewDrawer row={previewPanelRow} onClose={() => setPreviewPanelRow(null)} />
          ) : null}
        </div>
      ) : null}
      {processing ? <ProcessingOverlay run={processingRun && isRunActive(processingRun) ? processingRun : null} /> : null}
      <BillingCancellationModal
        open={cancellationModalOpen}
        onClose={() => setCancellationModalOpen(false)}
        onCompleted={() => loadData(selectedPeriodId || undefined)}
        session={session}
      />
    </div>
  );
}

/**
 * Bloquea toda la pantalla mientras se procesa un cierre de periodo o un
 * lote de facturacion.
 *
 * Motivo (incidente del 2026-10-01): el envio al SII es sincrono y tarda
 * minutos por documento; sin ninguna senal visible de que seguia trabajando,
 * el administrador reintento el cierre 11 veces en poco mas de una hora
 * pensando que no habia pasado nada, y 79 contratos quedaron facturados dos
 * veces, cada vez con un folio real nuevo. El backend ya bloquea una segunda
 * ejecucion mientras la anterior sigue en curso (ver
 * docs/pendientes/incidente_doble_facturacion_periodo_10_2026-10-06.md), pero
 * esta pantalla evita ademas que alguien lo intente: no tiene boton para
 * cerrarla ni reacciona a clics afuera, a proposito.
 *
 * Con una corrida unica en curso (`run`) la barra muestra su avance real:
 * documentos tomados durante la emision, documentos con veredicto durante la
 * validacion. Sin corrida (cierre todavia creando documentos) la barra es
 * indeterminada: mostrar un numero inventado seria peor que no mostrar ninguno.
 */
function ProcessingOverlay({ run }: { run: InvoiceProcessingRun | null }) {
  const validating = run?.phase === "validating";
  const done = run ? (validating ? run.issued + run.failed : run.attempted) : 0;
  const total = run ? (validating ? run.attempted : run.total) : 0;
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="alert"
      aria-live="assertive"
    >
      <style>{`
        @keyframes facturacion-progreso-indeterminado {
          0% { transform: translateX(-60%); }
          100% { transform: translateX(160%); }
        }
      `}</style>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center space-y-4">
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
          <i className="ri-loader-4-line text-3xl text-emerald-600 animate-spin" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-800">
            {run ? (validating ? "Validando en el SII…" : "Emitiendo documentos…") : "Procesando el periodo…"}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {run
              ? validating
                ? `Esperando el veredicto del SII: ${done} de ${total} documentos resueltos. Las boletas viajan en sobres y pueden tardar unos minutos.`
                : `${done} de ${total} documentos emitidos. Todo el período se procesa en un solo paso, no hay lotes que lanzar.`
              : "Preparando los documentos del período."}{" "}
            El proceso sigue en el servidor aunque cierres esta ventana; no lo vuelvas a lanzar desde otra pestaña.
          </p>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
          {run ? (
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${percent}%` }} />
          ) : (
            <div
              className="h-full w-1/3 rounded-full bg-emerald-500"
              style={{ animation: "facturacion-progreso-indeterminado 1.4s ease-in-out infinite" }}
            />
          )}
        </div>
        {run ? (
          <p className="text-xs text-gray-500">
            Emitidos {run.issued} · Errores/rechazos {run.failed} · Pendientes SII {run.pendingSii}
          </p>
        ) : null}
        <p className="text-xs text-gray-400">Esta pantalla se cierra sola cuando termine.</p>
      </div>
    </div>
  );
}

async function updateClosureMessage(
  session: AdminSession,
  invoiceIntegration: { providerName?: string; endpoint?: string; status?: string } | null,
  existingExtraJson: Record<string, unknown>,
  closureMessage: string,
): Promise<void> {
  await updateInvoiceIntegrationSettings(session, {
    providerName: invoiceIntegration?.providerName ?? "dte_cloud",
    endpoint: invoiceIntegration?.endpoint ?? getDteUrl(),
    status: invoiceIntegration?.status ?? "active",
    extraJson: {
      ...existingExtraJson,
      periodClosureMessage: closureMessage,
    },
  });
}

function StatCard(props: { label: string; value: number; color: string; bg: string; icon: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${props.bg} flex-shrink-0`}>
        <i className={`${props.icon} text-lg ${props.color}`} />
      </div>
      <div>
        <div className="text-xl font-bold text-gray-800">{props.value}</div>
        <div className="text-xs text-gray-500">{props.label}</div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-800">{value}</div>
    </div>
  );
}

function BillingPreviewDrawer({ row, onClose }: { row: BillingClosurePreviewItem; onClose: () => void }) {
  const variableItems = row.items.filter((item) => item.description !== "Consumo de agua por tramos" && !item.description.startsWith("Cargo fijo"));
  return (
    <aside className="fixed right-0 top-0 z-[60] h-screen w-full max-w-md overflow-y-auto border-l border-gray-200 bg-white shadow-2xl">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 px-5 py-4 backdrop-blur">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Vista previa</p>
            <h3 className="text-lg font-bold text-gray-900">{getDteDocumentLabel(row.documentType)}</h3>
            <p className="text-xs text-gray-500">Contrato {row.contractNumber}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      </div>

      <div className="space-y-4 px-5 py-4 text-sm">
        <section className="rounded-2xl border border-gray-200 p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Cliente / contrato</div>
          <PreviewLine label="Cliente" value={row.customerName || "-"} />
          <PreviewLine label="RUT" value={row.customerRut || "-"} />
          <PreviewLine label="Email" value={row.customerEmail || "-"} />
          <PreviewLine label="Contrato" value={row.contractNumber || "-"} />
          <PreviewLine label="Servicio" value={row.serviceNumber || "-"} />
          <PreviewLine label="Direccion" value={row.contractAddress || row.customerAddress || "-"} />
          <PreviewLine label="Sector" value={row.sector || "-"} />
          <PreviewLine label="Comuna" value={row.commune || "-"} />
          <PreviewLine label="Tarifa" value={row.tariffName || "-"} />
        </section>

        <section className="rounded-2xl border border-gray-200 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Consumo</div>
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
              {row.consumptionM3.toLocaleString("es-CL")} m3
            </span>
          </div>
          <PreviewAmount label="Calculo anterior" value={row.currentConsumptionCharge} />
          <PreviewAmount label="Calculo actualizado" value={row.updatedConsumptionCharge} emphasize />
          {row.consumptionBreakdown.length > 0 ? (
            <div className="mt-3 overflow-hidden rounded-lg border border-gray-100">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-2 py-1 text-left">Tramo</th>
                    <th className="px-2 py-1 text-right">m3</th>
                    <th className="px-2 py-1 text-right">Precio</th>
                    <th className="px-2 py-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {row.consumptionBreakdown.map((tier) => (
                    <tr key={tier.sortOrder} className="border-t border-gray-100">
                      <td className="px-2 py-1">#{tier.sortOrder}</td>
                      <td className="px-2 py-1 text-right">{tier.billedM3.toLocaleString("es-CL")}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(tier.unitPrice)}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(tier.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <section className="rounded-2xl border border-gray-200 p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Detalle de cobros</div>
          <PreviewAmount label="Gastos fijos" value={row.fixedCharge} />
          <PreviewAmount label="Consumo de agua" value={row.updatedConsumptionCharge} />
          <PreviewAmount label="Gastos variables" value={row.variableChargesTotal} />
          {variableItems.length > 0 ? (
            <div className="mt-2 space-y-1 rounded-lg bg-gray-50 p-2">
              {variableItems.map((item, index) => (
                <PreviewAmount key={`${item.description}-${index}`} label={item.description || "Gasto variable"} value={item.totalPrice} compact />
              ))}
            </div>
          ) : null}
          <PreviewAmount label="Gastos del mes" value={row.netAmount} emphasize />
          <PreviewAmount label="Saldo anterior" value={row.previousBalance} />
        </section>

        <section className="rounded-2xl border border-gray-900 bg-gray-950 p-4 text-white">
          <PreviewAmount label="Neto" value={row.netAmount} inverted />
          <PreviewAmount label="IVA" value={row.taxAmount} inverted />
          <PreviewAmount label="Total" value={row.totalAmount} inverted strong />
        </section>
      </div>
    </aside>
  );
}

function PreviewLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-medium text-gray-800">{value}</span>
    </div>
  );
}

function PreviewAmount({
  label,
  value,
  compact = false,
  emphasize = false,
  inverted = false,
  strong = false,
}: {
  label: string;
  value: number;
  compact?: boolean;
  emphasize?: boolean;
  inverted?: boolean;
  strong?: boolean;
}) {
  const textClass = inverted ? "text-white" : emphasize ? "text-blue-700" : "text-gray-800";
  return (
    <div className={`flex items-center justify-between gap-3 ${compact ? "py-0.5 text-xs" : "py-1.5"}`}>
      <span className={inverted ? "text-gray-300" : "text-gray-500"}>{label}</span>
      <span className={`${strong ? "text-xl" : "text-sm"} font-bold ${textClass}`}>{formatMoney(value)}</span>
    </div>
  );
}

function formatMoney(value: number): string {
  return `$${Math.round(value).toLocaleString("es-CL")}`;
}

function getDteDocumentLabel(value: DteDocumentType): string {
  return DTE_DOCUMENT_TYPES.find((type) => type.value === value)?.label ?? `DTE ${value}`;
}

function normalizeDteDocumentType(value?: string): DteDocumentType {
  return value === "33" || value === "34" || value === "39" || value === "41" ? value : "41";
}


